<template>
    <v-form v-model="valid" class="account-address-edit">
        <v-card v-bind="$attrs" class="account-address-edit__card" :disabled="false" @keypress.enter="save">
            <header class="account-address-edit__header">
                <div class="account-address-edit__mark" aria-hidden="true">
                    <v-icon>{{ mdiMapMarker }}</v-icon>
                </div>
                <div class="account-address-edit__heading">
                    <span class="account-address-edit__eyebrow">DELIVERY DETAILS</span>
                    <h3>Edit {{ label.toLowerCase() }} address</h3>
                    <p>Keep this address accurate for billing and device returns.</p>
                </div>
                <v-btn icon :aria-label="`Cancel editing ${label.toLowerCase()} address`" @click="cancel">
                    <v-icon>{{ mdiClose }}</v-icon>
                </v-btn>
            </header>

            <div class="account-address-edit__copy-option">
                <slot name="copy-from-billing" />
            </div>

            <v-card-text class="account-address-edit__fields">
                <span class="account-address-edit__section-label">CONTACT</span>
                <v-row>
                    <v-col cols="12" sm="6">
                        <v-text-field
                            ref="firstName"
                            v-model="updatedAddress.firstName"
                            v-bind="$attrs"
                            label="First Name"
                        />
                    </v-col>
                    <v-col cols="12" sm="6">
                        <v-text-field
                            ref="lastName"
                            v-model="updatedAddress.lastName"
                            v-bind="$attrs"
                            label="Last Name"
                        />
                    </v-col>
                </v-row>
                <v-text-field
                    ref="company"
                    v-model="updatedAddress.company"
                    v-bind="$attrs"
                    label="Company"
                />

                <span class="account-address-edit__section-label">ADDRESS</span>
                <v-text-field
                    ref="address-line1"
                    v-model="updatedAddress.line1"
                    v-bind="$attrs"
                    label="Address Line 1"
                />
                <v-text-field
                    ref="address-line2"
                    v-model="updatedAddress.line2"
                    v-bind="$attrs"
                    label="Address Line 2"
                />
                <v-row>
                    <v-col cols="12" sm="6">
                        <v-autocomplete
                            ref="country"
                            v-model="updatedAddress.country"
                            v-bind="$attrs"
                            :items="countries"
                            item-text="name"
                            item-value="countryCode"
                            label="Country"
                            placeholder="Country"
                            @change="countryChanged"
                        />
                    </v-col>
                    <v-col cols="12" sm="6">
                        <v-autocomplete
                            ref="state"
                            v-model="updatedAddress.state"
                            v-bind="$attrs"
                            item-text="name"
                            item-value="stateCode"
                            :loading="statesLoading"
                            :disabled="statesLoading || !updatedAddress.country"
                            :items="stateProvinces"
                            label="State/Province/Region"
                            placeholder="State"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" sm="6">
                        <v-text-field
                            ref="city"
                            v-model="updatedAddress.city"
                            v-bind="$attrs"
                            label="City"
                        />
                    </v-col>
                    <v-col cols="12" sm="6">
                        <v-text-field
                            ref="zip"
                            v-model="updatedAddress.postalCode"
                            v-bind="$attrs"
                            label="ZIP / Postal Code"
                        />
                    </v-col>
                </v-row>
            </v-card-text>

            <footer class="account-address-edit__actions">
                <v-btn
                    :block="$vuetify.breakpoint.smAndDown"
                    class="account-address-edit__cancel"
                    @click="cancel"
                >
                    Cancel
                </v-btn>
                <v-btn
                    :block="$vuetify.breakpoint.smAndDown"
                    class="account-address-edit__save"
                    color="primary"
                    :disabled="!valid"
                    @click="save"
                >
                    Save address
                </v-btn>
            </footer>
        </v-card>
    </v-form>
</template>

<script>
import Vue from 'vue'
import { mdiClose, mdiMapMarker } from '@mdi/js'

export default Vue.extend({
    name: `AccountEditAddress`,
    props: {
        copyFrom: {
            type: [Object, null],
            default: null
        },
        address: {
            type: Object,
            required: true
        },
        label: {
            type: String,
            default: () => `Address`
        }
    },
    data() {
        return {
            valid: null,
            statesLoading: false,
            citiesLoading: false,
            mdiClose,
            mdiMapMarker,
            updatedAddress: Object.assign(
                {},
                { country: `US`, ...this.address }
            ),
            previousAddress: {}
        }
    },
    computed: {
        countries() {
            return this.$store.getters[`location/getCachedCountries`]
        },
        cities() {
            return this.$store.getters[`location/getCachedCities`]({
                countryCode: this.updatedAddress.country,
                stateCode: this.updatedAddress.state
            })
        },
        stateProvinces() {
            return this.$store.getters[`location/getCachedStates`]({
                countryCode: this.updatedAddress.country
            })
        }
    },
    watch: {
        copyFrom(value) {
            if (value) {
                this.previousAddress = Object.assign({}, this.updatedAddress)
                this.updatedAddress = Object.assign({}, value)
            } else {
                this.updatedAddress = this.previousAddress
            }
        }
    },
    async mounted() {
        await this.$store.dispatch(`location/cacheStates`, {
            countryCode: this.updatedAddress.country
        })
    },
    methods: {
        async countryChanged(value) {
            if (value) {
                this.statesLoading = true
                try {
                    await this.$store.dispatch(`location/cacheStates`, {
                        countryCode: value
                    })
                } catch {
                    this.statesLoading = false
                } finally {
                    this.statesLoading = false
                }
            } else {
                this.updatedAddress.state = ``
            }
        },
        async getCities(event) {
            this.citiesLoading = true
            try {
                const [countryCode, stateCode] = event
                await this.$store.dispatch(`location/cacheCities`, {
                    countryCode,
                    stateCode
                })
            } finally {
                this.citiesLoading = false
            }
        },
        save() {
            this.$emit(`save`, {
                city: this.updatedAddress.city || ``,
                company: this.updatedAddress.company || ``,
                country: this.updatedAddress.country || ``,
                firstName: this.updatedAddress.firstName || ``,
                lastName: this.updatedAddress.lastName || ``,
                line1: this.updatedAddress.line1 || ``,
                line2: this.updatedAddress.line2 || ``,
                postalCode: this.updatedAddress.postalCode || ``,
                state: this.updatedAddress.state || ``
            })
        },
        cancel() {
            this.$emit(`cancel`)
        }
    }
})
</script>

<style scoped>
.account-address-edit {
    min-width: 0;
}
.account-address-edit__card {
    overflow: hidden;
    border-radius: 9px;
}
.account-address-edit__card.theme--dark {
    color: #f1f2f5;
    background: rgba(23, 25, 31, 0.38);
    border: 1px solid rgba(255, 255, 255, 0.1) !important;
    box-shadow: none !important;
}
.account-address-edit__header {
    display: flex;
    align-items: center;
    gap: 11px;
    padding: 16px 18px 14px;
}
.account-address-edit__card.theme--dark .account-address-edit__header {
    background: linear-gradient(110deg, rgba(219, 81, 32, 0.1), transparent 75%);
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.account-address-edit__mark {
    display: grid;
    width: 36px;
    height: 36px;
    flex: 0 0 36px;
    color: #fff;
    background: #db5120;
    border-radius: 10px;
    place-items: center;
}
.account-address-edit__mark .v-icon {
    color: inherit;
    font-size: 19px;
}
.account-address-edit__heading {
    min-width: 0;
    flex: 1;
}
.account-address-edit__eyebrow,
.account-address-edit__section-label {
    color: #b84820;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 1.2px;
}
.account-address-edit__card.theme--dark .account-address-edit__eyebrow,
.account-address-edit__card.theme--dark .account-address-edit__section-label {
    color: #f2a58b;
}
.account-address-edit__heading h3 {
    margin: 3px 0 2px;
    font-size: 15px;
    font-weight: 600;
}
.account-address-edit__heading p {
    margin: 0;
    color: #747985;
    font-size: 10px;
    line-height: 1.5;
}
.account-address-edit__card.theme--dark .account-address-edit__heading h3 {
    color: #f4f5f8;
}
.account-address-edit__card.theme--dark .account-address-edit__heading p {
    color: #aeb5c3;
}
.account-address-edit__header > .v-btn {
    color: #8b909a;
}
.account-address-edit__card.theme--dark .account-address-edit__header > .v-btn {
    color: #aeb5c3;
}
.account-address-edit__copy-option {
    padding: 6px 18px 0;
}
.account-address-edit__copy-option ::v-deep .v-label {
    font-size: 11px;
}
.account-address-edit__fields {
    padding: 12px 18px 4px;
}
.account-address-edit__section-label {
    display: block;
    margin: 9px 0 5px;
}
.account-address-edit__card.theme--dark .account-address-edit__fields ::v-deep .v-label {
    color: #aeb5c3;
}
.account-address-edit__card.theme--dark .account-address-edit__fields ::v-deep input {
    color: #f1f2f5;
}
.account-address-edit__card.theme--dark .account-address-edit__fields ::v-deep .v-input__slot fieldset {
    border-color: rgba(255, 255, 255, 0.18);
}
.account-address-edit__card.theme--dark .account-address-edit__fields ::v-deep .v-input--is-focused .v-label {
    color: #ff9a74;
}
.account-address-edit__card.theme--dark .account-address-edit__fields ::v-deep .v-input--is-focused .v-input__slot fieldset {
    border-color: #db5120;
}
.account-address-edit__actions {
    display: flex;
    justify-content: flex-end;
    gap: 9px;
    padding: 4px 18px 16px;
}
.account-address-edit__actions .v-btn {
    min-height: 36px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.2px;
    text-transform: none;
}
.account-address-edit__cancel.v-btn {
    color: #6b707a;
    border: 1px solid rgba(56, 62, 74, 0.2);
}
.account-address-edit__card.theme--dark .account-address-edit__cancel.v-btn {
    color: #c7cbd4;
    border-color: rgba(255, 255, 255, 0.16);
}
.account-address-edit__save.v-btn {
    color: #fff;
    background: #db5120;
}
.account-address-edit__save.v-btn:hover {
    background: #e76638;
}
@media (max-width: 600px) {
    .account-address-edit__actions {
        flex-direction: column-reverse;
    }
}
</style>
